---
title: Make a persona answer only from its documents
summary: There's no single switch — combine an explicit rule, retrieval thresholds, and temperature to keep answers grounded.
visibility: authenticated
audience: author-admin
section: How-to
order: 55
last_verified: 2026-07-21
owner: docs
---

# Make a persona answer only from its documents

A common goal is a persona that answers strictly from your knowledge base and declines
when the documents don't cover a question. There is **no single "documents only" toggle**
— grounding is the combined effect of an explicit rule in the **Workshop**, plus a few
retrieval settings in **Persona Settings**' **AI Settings** tab. Tune them together.

## 1. Tell it so, in a Rules & constraints block

Click the persona card's **Edit** button to open the **Workshop**. If you don't already
have one, switch to **Advanced** mode (the **Simple ⇄ Advanced** toggle at the top),
open the **Rules & guardrails** section, and add a **Rules & constraints** block. Be
explicit. For example:

> Answer only using the provided context from the knowledge base. If the context does not
> contain the answer, say you don't have that information rather than guessing. Do not use
> outside knowledge.

The model follows these instructions, so a clear, firm rule does most of the work. You
can check the exact wording your persona actually sends — this block included — any
time via the Workshop's **Advanced** section, **View composed prompt →** (read-only; see
[Advanced authoring in the Workshop](/help/advanced/use-the-workshop)).

## 2. Raise the relevance bar

**Score Threshold** (0–1) is the minimum relevance a retrieved passage must have to be
used. The default is `0.7`. Raising it (say to `0.8`) discards weakly-related passages, so
the persona has less marginal material to wander into. If answers start missing things
they should know, ease it back down.

## 3. Tune how many passages it sees

**Top K Sources** (1–20, default `5`) is how many passages are retrieved per question. A
moderate value gives enough context without burying the answer in loosely-related text.

## 4. Keep creativity low

**Temperature** (0–2, default `0.7`) controls how freely the model writes. Lower values
(for example `0.2`) keep it closer to the retrieved text and reduce the chance of
invented detail.

## 5. Make sure it's actually retrieving

Grounding only works if retrieval happens:

- The persona must point at a **Graph ID** that contains the relevant documents (see
  [Connect a persona to its knowledge](/help/how-to/connect-knowledge-rag)).
- The persona must use a **retrieval-enabled model**. On this platform the RAG server
  performs retrieval when the configured model is a retrieval model; a plain
  (non-retrieval) model answers from general knowledge with no documents. If you're unsure
  which to pick, leave **Model** on the server default — on most deployments it's set up to
  retrieve; ask your administrator if you're not sure.

## Verify

Ask two questions: one your documents clearly cover, and one they don't. The first should
answer with **citations**; the second should politely decline rather than make something
up. Adjust the **Rules & constraints** block and **Score Threshold** until both behave.
